import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

import { TokenContext, TokenContextValue } from '../contexts/token-context';

interface Props extends PropsWithChildren {
   defaultToken?: string;
}

async function getToken(forceRefresh: boolean) {
   try {
      const response = await fetch(`/api/auth/token?refresh=${forceRefresh}`);
      if (!response.ok) {
         return undefined;
      }

      const data = await response.json();
      return data.accessToken as string;
   } catch (error) {
      return undefined;
   }
}

function getExpiresAtFromToken(token: string) {
   try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return undefined;
      return decoded.exp * 1000;
   } catch (error) {
      return undefined;
   }
}

export const TokenProvider: FC<Props> = ({ defaultToken, children }) => {
   const [accessToken, setAccessToken] = useState(defaultToken);
   const [expiresAt, setExpiresAt] = useState<number | undefined>(defaultToken ? getExpiresAtFromToken(defaultToken) : undefined);
   const [isLoading, setIsLoading] = useState(!defaultToken);

   const fetchToken = useCallback(async (forceRefresh = false) => {
      const token = await getToken(forceRefresh);
      if (!token) {
         setAccessToken(undefined);
         setExpiresAt(undefined);
         return;
      }

      setAccessToken(token);
      setExpiresAt(getExpiresAtFromToken(token));
   }, []);

   useEffect(() => {
      const init = async () => {
         if (accessToken) return;
         await fetchToken();
         setIsLoading(false);
      };

      init();
   }, [fetchToken]);

   useEffect(() => {
      if (!expiresAt) return undefined;

      const timeUntilRefresh = expiresAt - Date.now();
      if (timeUntilRefresh <= 0) {
         fetchToken();
         return undefined;
      }

      const timerId = setTimeout(fetchToken, timeUntilRefresh);
      return () => clearTimeout(timerId);
   }, [expiresAt, fetchToken]);

   const tokenContextValue = useMemo<TokenContextValue>(
      () => ({
         accessToken,
         isLoading,
         refreshToken: () => fetchToken(true)
      }),
      [accessToken, isLoading, fetchToken]
   );

   return <TokenContext.Provider value={tokenContextValue}>{children}</TokenContext.Provider>;
};
